/**
 * Dynamic Syncfusion theme switcher.
 *
 * Syncfusion ships separate CSS bundles for light ("material") and dark
 * ("material-dark") themes. We statically import the light theme in main.tsx
 * (bundled by Vite), and lazily inject the dark theme as `<link>` tags when
 * monday.com's host switches to dark mode.
 *
 * Using Vite's `?url` import suffix we get the hashed, production-ready URL
 * for every dark CSS file at build time, so this works in both dev and prod.
 */

import baseDarkUrl from '@syncfusion/ej2-base/styles/material-dark.css?url';
import buttonsDarkUrl from '@syncfusion/ej2-buttons/styles/material-dark.css?url';
import calendarsDarkUrl from '@syncfusion/ej2-calendars/styles/material-dark.css?url';
import dropdownsDarkUrl from '@syncfusion/ej2-dropdowns/styles/material-dark.css?url';
import inputsDarkUrl from '@syncfusion/ej2-inputs/styles/material-dark.css?url';
import navigationsDarkUrl from '@syncfusion/ej2-navigations/styles/material-dark.css?url';
import popupsDarkUrl from '@syncfusion/ej2-popups/styles/material-dark.css?url';
import scheduleDarkUrl from '@syncfusion/ej2-schedule/styles/material-dark.css?url';

const DARK_CSS_URLS = [
  baseDarkUrl,
  buttonsDarkUrl,
  calendarsDarkUrl,
  dropdownsDarkUrl,
  inputsDarkUrl,
  navigationsDarkUrl,
  popupsDarkUrl,
  scheduleDarkUrl,
];

const LINK_ID_PREFIX = 'sf-dark-theme-';

let darkThemeActive = false;

/** Inject or remove Syncfusion material-dark CSS links. */
export function setSyncfusionDarkTheme(enabled: boolean): void {
  if (enabled === darkThemeActive) return;
  darkThemeActive = enabled;

  if (enabled) {
    const fragment = document.createDocumentFragment();
    DARK_CSS_URLS.forEach((url, i) => {
      const link = document.createElement('link');
      link.id = `${LINK_ID_PREFIX}${i}`;
      link.rel = 'stylesheet';
      link.href = url;
      fragment.appendChild(link);
    });
    document.head.appendChild(fragment);
  } else {
    DARK_CSS_URLS.forEach((_, i) => {
      document.getElementById(`${LINK_ID_PREFIX}${i}`)?.remove();
    });
  }
}
